/**
 * Recurrence expansion — the maths behind a flexible-working roster
 * (`plans/phase-3-availability-fwa.md` §1, decision 2).
 *
 * Two rule kinds cover everything the brief asks for (Oscar's decision 1):
 *
 * - `INTERVAL_DAYS(n)` — every n-th day from the rule's start, inclusive of
 *   the start itself.
 * - `WEEKLY(weekday, every n weeks)` — every `weekday`, every n weeks, where
 *   n=1 is "every Friday" and n=2 is the brief's "every 2nd Sunday", meaning
 *   *fortnightly*, not the second Sunday of the month. **The phase is anchored
 *   to the rule's own start date**, so two fortnightly rules with different
 *   starts can sit on opposite phases, and that is correct: an anchor shared
 *   with anything but the rule itself would quietly move a user's Sundays.
 *
 * Expansion is computed here, in TypeScript, and **never in SQL** — decision 2.
 * Saving or editing a rule writes explicit `shifts` rows out to its end date
 * or the horizon; the calendar extends them lazily with
 * `ensureRecurrenceCoverage`. This module therefore only ever answers "which
 * dates does this rule occupy inside this window?", as a pure function.
 *
 * **Validation throws rather than guesses — the same stance as `blockIndex`.**
 * A half-specified rule (a `WEEKLY` with no weekday, an `INTERVAL_DAYS` that
 * also carries a weekday) is a bug upstream, and the failure mode this repo
 * refuses everywhere is a wrong-but-plausible roster arriving looking exactly
 * like a right one. `validateRecurrenceRule` is exported and called by both
 * expand functions, so a caller composing an insert can reject a bad rule
 * before it reaches the database.
 *
 * **Purity.** No DOM, no React, no Supabase, no I/O — the same boundary
 * `boundaries.test.ts` enforces over this directory. The only imports are
 * `./civil` (already pure) and `./types`.
 */

import { addDays, dayNumber, dayOfWeek, floorMod } from './civil'
import type { DateRange, IsoDate, ShiftCode } from './types'

/**
 * `addDays` anchored to the Unix epoch, converting a `dayNumber` result back
 * to an `IsoDate`. `dayNumber('1970-01-01')` is 0 by construction, so this is
 * the exact inverse of `dayNumber` — the round trip `civil.ts` never needed
 * until recurrence arithmetic started working in day numbers for weeks at a
 * time.
 */
const EPOCH: IsoDate = '1970-01-01'

/** Day-number → `IsoDate`. The inverse of `dayNumber`. */
function toIsoDateFromDayNumber(day: number): IsoDate {
  return addDays(EPOCH, day)
}

/**
 * A recurrence rule, mirroring migration `0008_recurrences`'s columns in
 * camelCase. Exactly one of `intervalDays` / (`weekday` + `weeksInterval`) is
 * meaningful per row, keyed by `kind` — the migration's `CHECK` enforces it in
 * the database and `validateRecurrenceRule` enforces it before anything
 * reaches the database.
 *
 * `weekday` is 0 = Sunday … 6 = Saturday — `civil.ts`'s `dayOfWeek`
 * convention, not `Date`'s raw `getDay()` on a local-timezone date.
 *
 * `exceptionDates` are the *rule's own* skipped dates — a fortnightly Sunday
 * with one Sunday off — as distinct from `day_exceptions`, which is about the
 * person on the day. They exist because editing a rule regenerates its rows
 * and would otherwise resurrect a deleted occurrence.
 */
export interface RecurrenceRule {
  /** The `recurrences` row's uuid, once the rule has one. */
  id?: string
  kind: 'INTERVAL_DAYS' | 'WEEKLY'
  /** Required iff `kind` is `INTERVAL_DAYS`; whole days between occurrences. */
  intervalDays?: number | null
  /** Required iff `kind` is `WEEKLY`; 0 = Sunday … 6 = Saturday. */
  weekday?: number | null
  /** Required iff `kind` is `WEEKLY`; 1 = weekly, 2 = fortnightly, … */
  weeksInterval?: number | null
  shiftCode: ShiftCode
  /** First possible occurrence date, inclusive. */
  startDate: IsoDate
  /** Last possible occurrence date, inclusive; `null`/omitted = open-ended. */
  endDate?: IsoDate | null
  /** Dates the rule itself skips, even though the pattern lands on them. */
  exceptionDates: IsoDate[]
}

/**
 * One occurrence as a shift row ready for insertion: `ExplicitShift` minus
 * `id` and `status`. `src/lib/roster-data.ts` adds both from the database —
 * the id comes from the inserted row, the status from the `shifts` default
 * (`ROSTERED`) — which is why this type deliberately stops where it does: a
 * client-side id or status would be a second source of truth for things only
 * the database can say.
 */
export interface RecurrenceShiftRow {
  date: IsoDate
  code: ShiftCode
  origin: 'FWA_RECURRING'
}

/**
 * Reject a malformed rule. `TypeError` for a wrong *shape* (a required partner
 * field missing, or a forbidden one present), `RangeError` for a right-shaped
 * field with an impossible value (and for malformed dates, which
 * `parseIsoDate` inside `dayNumber` throws as `RangeError` — let it).
 *
 * Called by both expand functions, so every expansion validates first; it is
 * exported for callers composing inserts, so a bad rule is rejected before it
 * reaches the database rather than after. The value bounds here mirror
 * migration `0008`'s CHECKs exactly (`interval_days <= 366`,
 * `weeks_interval <= 52`), so a rule that passes this validator cannot be
 * rejected by the database on those columns — the mismatch otherwise surfaces
 * on the edit path, after the rule's generated rows have already been deleted.
 */
export function validateRecurrenceRule(rule: RecurrenceRule): void {
  if (rule.kind === 'INTERVAL_DAYS') {
    if (rule.intervalDays == null) {
      throw new TypeError(`INTERVAL_DAYS rule needs intervalDays, got ${JSON.stringify(rule.intervalDays)}`)
    }
    if (!Number.isInteger(rule.intervalDays) || rule.intervalDays < 1 || rule.intervalDays > 366) {
      throw new RangeError(`intervalDays must be an integer from 1 to 366, got ${JSON.stringify(rule.intervalDays)}`)
    }
    if (rule.weekday != null) {
      throw new TypeError(`INTERVAL_DAYS rule must not carry weekday, got ${JSON.stringify(rule.weekday)}`)
    }
    if (rule.weeksInterval != null) {
      throw new TypeError(`INTERVAL_DAYS rule must not carry weeksInterval, got ${JSON.stringify(rule.weeksInterval)}`)
    }
  } else if (rule.kind === 'WEEKLY') {
    if (rule.weekday == null) {
      throw new TypeError(`WEEKLY rule needs weekday, got ${JSON.stringify(rule.weekday)}`)
    }
    if (!Number.isInteger(rule.weekday) || rule.weekday < 0 || rule.weekday > 6) {
      throw new RangeError(`weekday must be 0 (Sunday)–6 (Saturday), got ${JSON.stringify(rule.weekday)}`)
    }
    if (rule.weeksInterval == null) {
      throw new TypeError(`WEEKLY rule needs weeksInterval, got ${JSON.stringify(rule.weeksInterval)}`)
    }
    if (!Number.isInteger(rule.weeksInterval) || rule.weeksInterval < 1 || rule.weeksInterval > 52) {
      throw new RangeError(`weeksInterval must be an integer from 1 to 52, got ${JSON.stringify(rule.weeksInterval)}`)
    }
    if (rule.intervalDays != null) {
      throw new TypeError(`WEEKLY rule must not carry intervalDays, got ${JSON.stringify(rule.intervalDays)}`)
    }
  } else {
    // Unreachable for anything that type-checked, reachable for anything that
    // arrived as JSON — and the CHECK constraint is not there yet to catch it.
    throw new TypeError(`Unknown recurrence kind: ${JSON.stringify(rule.kind)}`)
  }

  // Malformed dates throw RangeError from inside dayNumber (via parseIsoDate's
  // round-trip check) — '2026-02-30' is refused, not silently 2 March.
  const startNum = dayNumber(rule.startDate)
  if (rule.endDate != null) {
    const endNum = dayNumber(rule.endDate)
    if (endNum < startNum) {
      throw new RangeError(`endDate ${rule.endDate} is before startDate ${rule.startDate}`)
    }
  }
  for (const date of rule.exceptionDates ?? []) {
    dayNumber(date)
  }
}

/**
 * Every occurrence date of `rule` inside `∩ [startDate, endDate?]` ∩ `range`
 * — all three bounds inclusive — minus `exceptionDates`.
 *
 * - `INTERVAL_DAYS(n)`: every n-th day from `startDate`, inclusive of the
 *   start. A window that begins mid-pattern keeps the pattern's phase: the
 *   first in-window occurrence is on the grid, never restarted at the window
 *   edge.
 * - `WEEKLY(weekday, n)`: the first occurrence is the first `weekday` **on or
 *   after** `startDate` (the start date itself when it is that weekday); then
 *   every `n * 7` days from *that* first occurrence, so the phase belongs to
 *   the rule, not to the calendar. The days-to-next-weekday arithmetic uses
 *   `floorMod`, never `%` — `weekday − dayOfWeek(startDate)` goes negative
 *   whenever the target weekday is earlier in the week than the start, and a
 *   plain `%` would hand back a negative offset and a first occurrence
 *   *before* the start.
 *
 * An inverted range (or a window that misses the rule entirely) yields `[]`,
 * not a throw — the *rule* being impossible is a bug; the *window* being empty
 * is Tuesday. Output is sorted ascending with no duplicates (the iteration
 * order guarantees both; the sort is a stated property, not an accident).
 */
export function expandRecurrence(rule: RecurrenceRule, range: DateRange): IsoDate[] {
  validateRecurrenceRule(rule)

  const startNum = dayNumber(rule.startDate)
  // The rule's own last day, if it has one, clipped by the requested window.
  const lastNum = Math.min(dayNumber(range.to), rule.endDate != null ? dayNumber(rule.endDate) : Infinity)
  // The first day anything can appear: the later of the window start and the
  // rule's own start.
  const firstNum = Math.max(dayNumber(range.from), startNum)
  if (firstNum > lastNum) return []

  const occurrences: number[] = []

  if (rule.kind === 'INTERVAL_DAYS') {
    // Validated non-null and >= 1 by validateRecurrenceRule.
    const interval = rule.intervalDays as number
    // The first occurrence at or after firstNum, on the start-anchored grid:
    // ceil() lands on the grid point when firstNum is between two, and on
    // firstNum itself when firstNum is one (startNum + 0 also satisfies this).
    const k = Math.ceil((firstNum - startNum) / interval)
    for (let day = startNum + k * interval; day <= lastNum; day += interval) {
      occurrences.push(day)
    }
  } else {
    // Validated non-null by validateRecurrenceRule.
    const weekday = rule.weekday as number
    const weeksInterval = rule.weeksInterval as number

    // floorMod, never %: weekday − dayOfWeek(startDate) is negative whenever
    // the target weekday is earlier in the week than the start date.
    const daysToFirst = floorMod(weekday - dayOfWeek(rule.startDate), 7)
    const firstEver = startNum + daysToFirst // first occurrence, >= startDate
    const step = weeksInterval * 7

    let m = 0
    if (firstEver < firstNum) {
      m = Math.ceil((firstNum - firstEver) / step)
    }
    for (let day = firstEver + m * step; day <= lastNum; day += step) {
      occurrences.push(day)
    }
  }

  const skipped = new Set((rule.exceptionDates ?? []).map(dayNumber))
  const kept = occurrences.filter((day) => !skipped.has(day))
  kept.sort((a, b) => a - b)
  return kept.map(toIsoDateFromDayNumber)
}

/**
 * The same dates as `RecurrenceShiftRow`s, ready for `roster-data.ts` to
 * insert into `shifts` (`on conflict (owner_user_id, work_date, shift_code)
 * do nothing`, so a hand-added `FWA_MANUAL` shift on a generated date wins and
 * the skip is countable). See `RecurrenceShiftRow` for why `id` and `status`
 * are deliberately absent.
 */
export function expandRecurrenceToRows(rule: RecurrenceRule, range: DateRange): RecurrenceShiftRow[] {
  return expandRecurrence(rule, range).map((date) => ({
    date,
    code: rule.shiftCode,
    origin: 'FWA_RECURRING',
  }))
}
